/**
 * grass_rules.ts — WHERE the grass grows, how tall, and what colour (2026-09-25, visual-realism R2). Pure.
 *
 * One sample of the rules: the biome weights, the canopy over it, the road/pad/water/snow keep-outs,
 * the slope and a meadow-patch noise go in; the blade DENSITY (0..1 of the layer lattice), the mean
 * blade HEIGHT (m) and the TURF colour (linear, lib/grass/turf_core.ts — the colour function the
 * ground shares, evaluated on the ground's own vertex tint / dryness / wetness when they are resident)
 * come out. The grass field (grass_field.ts) evaluates this on a 2 m lattice and
 * interpolates it per 0.5 m texel; the harness pins the keep-outs.
 *
 * KEEP-OUTS (a blade must never stand on them): settlement carriageways and worn tracks (the road
 * mask above 0.22 is bare; 0.08–0.22 is the trampled verge), building pads / port hulls / placed
 * structures / cave aprons (`blocked`), water (the sea, a baked lake, a river — nothing below the
 * surface + 0.15 m), the snow band, and cliffs (slope past 0.95).
 */
import { GRASS_DENSITY, GRASS_HEIGHT_M, perBiome } from './turf_color.js';
import { turfColor, turfColorAt } from './turf_core.js';

export interface GrassSample {
  /** Six biome weights in BIOMES order. */
  w: ArrayLike<number>;
  /** Forest density 0..1 (lib/forest_field): the canopy shades the meadow out. */
  forest: number;
  /** Road surface mask 0..1 (terrain_stamp.roadSurfaceMask). */
  road: number;
  /** A pad, port hull, structure footprint or cave apron covers the point. */
  blocked: boolean;
  /** Rendered ground height (m) and its rise/run. */
  h: number;
  slope: number;
  /** The Commons sea level and the local lake/river surface (null = none). */
  sea: number;
  water: number | null;
  /** Meadow-patch noise 0..1 (~30 m): bare, thin and lush patches. */
  patch: number;
  /** Dryness noise 0..1 (~90 m). */
  dryN: number;
  /**
   * The GROUND's own climate channel at the point, read off the resident chunk mesh (grass_driver `groundAt`):
   * [tint r, tint g, tint b, dryness, wetness] — the exact inputs the ground's turf layer shades with, so the
   * blade and the turf under it take ONE colour (turf_core.ts). Null where no v2 chunk data is resident: the
   * colour then falls back to the tint of `w` and this rule's own dryness / wetness.
   */
  ground?: ArrayLike<number> | null;
}
export interface GrassOut { density: number; bladeH: number; rgb: [number, number, number]; dry: number; wet: number }

/** Snow band (m, absolute): no grass above it (the ground's permanent cap starts at 320 m). */
export const GRASS_SNOW_M = 322;
function ss(a: number, b: number, x: number): number { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); }
function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v; }

/** Wetness proxy 0..1: a water margin (0–6 m above a lake/river surface) or low ground just above the sea. */
export function grassWetness(s: Pick<GrassSample, 'h' | 'sea' | 'water'>): number {
  const byWater = s.water === null ? 0 : 1 - ss(0.3, 6, s.h - s.water);
  const bySea = 1 - ss(1.5, 9, s.h - s.sea);
  return clamp01(Math.max(byWater, bySea * 0.6));
}
/** Dryness 0..1: altitude, slope (shed water) and the dryness noise, minus the wet margin. */
export function grassDryness(s: Pick<GrassSample, 'h' | 'sea' | 'slope' | 'dryN'>, wet: number): number {
  return clamp01(0.3 * s.dryN + 0.45 * ss(s.sea + 70, s.sea + 240, s.h) + 0.35 * ss(0.25, 0.8, s.slope) - 0.7 * wet);
}

export function grassRule(s: GrassSample, out: GrassOut = { density: 0, bladeH: 0, rgb: [0, 0, 0], dry: 0, wet: 0 }): GrassOut {
  const wet = grassWetness(s);
  const dry = grassDryness(s, wet);
  const g = s.ground;
  if (g) turfColorAt(g[0], g[1], g[2], g[3], g[4], out.rgb); // the ground's own inputs → the turf's own colour
  else turfColor(s.w, dry, wet, out.rgb);
  out.dry = dry; out.wet = wet;
  const surface = Math.max(s.sea, s.water ?? -Infinity);
  let d = s.blocked ? 0 : perBiome(GRASS_DENSITY, s.w);
  if (d > 0) {
    d *= 1 - 0.88 * clamp01(s.forest);           // the forest floor: litter, a few blades
    d *= 1 - ss(0.55, 0.95, s.slope);             // cliffs are bare
    d *= 1 - ss(0.08, 0.22, s.road);              // carriageways bare, verges trampled
    d *= ss(0.15, 0.6, s.h - surface);            // nothing under water or on the wet line
    d *= 1 - ss(GRASS_SNOW_M - 22, GRASS_SNOW_M, s.h); // the snow band
    d *= 0.28 + 0.72 * ss(0.22, 0.58, s.patch);   // bare, thin and lush patches
    d *= 1 - 0.45 * dry * ss(0.4, 1, s.dryN);     // the driest ground thins
  }
  out.density = clamp01(d);
  out.bladeH = perBiome(GRASS_HEIGHT_M, s.w) * (0.78 + 0.5 * s.patch) * (1 - 0.6 * clamp01(s.road)) * (1 - 0.4 * Math.min(1, s.slope))
    * (1 + 0.35 * wet) * (1 - 0.45 * clamp01(s.forest)) * (1 - 0.3 * dry);
  return out;
}
